import { Injectable } from '@angular/core';
import { MessageService } from '@services/helpers/message-service/message.service';

import { AppConstants } from '@models/appConstants';
import { RandomStringGeneratorProvider } from '@services/helpers/random-string-generator';
import { UtilsServiceProvider } from '@services/utils/utils-service';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { DatePipe } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';

interface AttachmentsData {
	attachments: Array<any>;
	filesSize: number;
}

interface MessageData {
	emailSubject: string;
	emailText: string;
	emailTo: string;
	emailCc: string;
	attachmentsData?: any;
}

interface Attachments {
	storedFiles: Array<any>;
	uploadedFiles: Array<any>;
}

@Injectable()
export class EmailServiceProvider {
	private emptyAttachmentsData: AttachmentsData = {
		attachments: [],
		filesSize: 0
	};

	emailFn: Function = null;

	clientToCheckSubscription: BehaviorSubject<any> = new BehaviorSubject(null);

	constructor(
		private messageService: MessageService,
		private randomStringGenerator: RandomStringGeneratorProvider,
		private utilsService: UtilsServiceProvider,
		public datePipe: DatePipe,
		private sanitizer: DomSanitizer
	) {}

	get clientToCheckSubscription$(): Observable<any> {
		return this.clientToCheckSubscription.asObservable();
	}

	emitClientToCheckSubscription(client: any): void {
		this.clientToCheckSubscription.next(client);
	}

	setMessageData(data, client): MessageData {
		let { emailSubject, emailText, mailSignature } = data;
		if (mailSignature) {
			emailText += `<br><br>${mailSignature}`;
		}
		return {
			emailSubject: emailSubject || '',
			emailText: emailText || '',
			emailTo: client.email || '',
			emailCc: '',
			attachmentsData: JSON.parse(JSON.stringify(this.emptyAttachmentsData))
		};
	}

	getClientEmail(client): string {
		const stringEmail = Object.assign(client.name);
		return stringEmail.substring(
			stringEmail.indexOf('(') + 1,
			stringEmail.length - 1
		);
	}

	prepareAttachments(attachmentsData): Attachments {
		return {
			storedFiles: attachmentsData.attachments.filter(file => file.id),
			uploadedFiles: attachmentsData.attachments.filter(file => !file.id)
		};
	}

	isAttachmentsValid(attachmentsData): boolean {
		const maxSize = 10; // 10Mb
		if (attachmentsData.filesSize > maxSize) {
			this.messageService.showToaster('w_0', 'attachments');
			return false;
		}
		return true;
	}

	prepareSignatureToShow(value: string): string {
		const imgTag = '<img id="signatureImg';
		if (!value.includes(imgTag)) {
			return value;
		}
		const signatureLink = 'SN/images/signature/';
		const updatedLink = `${AppConstants.HOST}/${signatureLink}`;
		if (value.includes(updatedLink)) {
			return value;
		}
		return value.replace(signatureLink, updatedLink);
	}

	resetSignatureLink(value: string) {
		const imgTag = '<img id="signatureImg';
		if (!value.includes(imgTag)) {
			return value;
		}

		const imgIndex = value.indexOf(imgTag);
		const closeIndex = value.indexOf('>', imgIndex);
		const addedServerString = /^(https?:\/\/).+\/$/;

		const fullImgTag = value.substring(imgIndex, closeIndex + 1).trim();
		const newImgTag = fullImgTag.replace(addedServerString, '');

		return value.replace(fullImgTag, newImgTag);
	}

	addMaiStyling(emailText: string): string {
		const emptyParagraph = '<p></p>';

		if (!emailText) {
			return emailText || '';
		}

		if (emailText === emptyParagraph) {
			return '';
		}

		const tmpId = `textAngular_styling_${this.randomStringGenerator.getMixedString(
			9
		)}`;
		const $tmpId = `#${tmpId}`;
		const tempIdEl = document.getElementById($tmpId);

		if (!tempIdEl) {
			return emailText;
		}

		const body = document.getElementsByTagName('body')[0];
		body.innerHTML = `<div id="${tmpId}" class="hide"></div>`;
		tempIdEl.innerHTML = emailText;

		this.mailStyling($tmpId);

		const tempTextEl = document.getElementById($tmpId);
		const message = tempTextEl.innerHTML;
		tempTextEl.remove();

		return message;
	}

	private mailStyling = (id: string): void => {
		const styleObj = {
			P: 'margin:0 0 2px;',
			LI: 'margin-bottom:1px;',
			SPAN: 'padding:0;',
			DIV: 'margin:0;',
			BLOCKQUOTE:
				'margin: 0 0 0 0.8ex; border-left: 1px #ccc solid; padding-left: 1ex;'
		};

		const defaultStyle = 'font-size:14px;color:#000000;line-height:22px;';
		const allTags = document.getElementById(id).querySelectorAll('*');

		document.getElementById('.edit-link__tips').remove();
		document
			.getElementById('.edit-link__elem')
			.classList.remove('edit-link__elem');

		let i = 0;
		const max = allTags.length;

		for (; i < max; i += 1) {
			const thisTag = allTags[i];

			let tagStyle = '';
			const elementStyle = window.getComputedStyle(thisTag);
			const textAlign = elementStyle.getPropertyValue('text-align');

			if (textAlign) {
				tagStyle += `text-align:${textAlign};`;
			}
			const thatTag = thisTag.tagName;

			tagStyle += styleObj[thatTag] || '';
			tagStyle += defaultStyle;

			if (thatTag === 'A') {
				tagStyle += 'color:#337ab7;';
			}

			thisTag.setAttribute('style', tagStyle);
		}
	};

	getEmailTextReply(emailText: string, text: string): string {
		return `<br><br>
                <div class="gmail_quote color-grey">${emailText}
                    <blockquote class="gmail_quote color-grey">${text}</blockquote>
                </div>`;
	}

	escapeEmailDetailsHtml(str: string): string {
		return str
			.replace(/&/g, '&amp;')
			.replace(/>/g, '&gt;')
			.replace(/</g, '&lt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;')
			.replace(/`/g, '&#96;');
	}

	escapeEmailTextHtml(text: string): string {
		let matchedText: any = text.replace(/[\r\n]+/g, ' ');
		matchedText = /<body.*>(.*?)<\/body>/gim.exec(matchedText);
		return matchedText ? matchedText[0] : text;
	}

	getReplyText(mailData): string {
		return `<br/><br/> 
                 ${mailData.emailTextReply} 
                 <br/><br/> 
                 ${mailData.mailSignature || ''} 
                `;
	}

	getReplyDateInFormat(date: string): string {
		const dateFormatForReply = "EEE, MMM dd, yyyy 'at' HH:mm a";
		return this.datePipe.transform(date, dateFormatForReply);
	}

	getFormattedReplyData(data) {
		const { subject, to, cc, date, allFromName, text } = data;
		const formattedDate = this.getReplyDateInFormat(date);
		const mailData = JSON.parse(JSON.stringify(data));

		mailData.emailSubject = subject;
		mailData.emailTo = to;
		mailData.emailCc = cc;
		mailData.reply = true;
		mailData.validMsg = true;

		try {
			!!this.sanitizer.bypassSecurityTrustHtml(text);

			const emailDetails = this.escapeEmailDetailsHtml(
				`On ${formattedDate} ${allFromName} wrote:`
			);
			const matchedText = this.escapeEmailTextHtml(text);
			mailData.emailTextReply = this.getEmailTextReply(
				emailDetails,
				matchedText
			);
		} catch (err) {
			console.log('error ', err);
			mailData.emailTextReply =
				'Oops! This message is unsupported and cannot be displayed in Studio Ninja. Please view this message in your email provider';
			mailData.validMsg = false;
		}
		mailData.emailText = this.getReplyText(mailData);
		return mailData;
	}

	checkIfEmailAllowedWeb(contact, func, thisRef, argsArr) {
		this.emailFn = this.utilsService.wrapFunction(func, thisRef, argsArr);

		if (contact && contact.status === 'UNSUBSCRIBED') {
			this.utilsService.closeAllModalsAndOpenNew('#resubscribeClientModal');
		} else {
			this.callLastEmailFn();
		}
	}

	checkIsCCSubscribedWeb(contact, func, thisRef, argsArr) {
		this.emailFn = this.utilsService.wrapFunction(func, thisRef, argsArr);

		if (contact && contact.status === 'UNSUBSCRIBED') {
			$('#resubscribeClientModal').modal('show');
		} else {
			this.callLastEmailFn();
		}
	}

	callLastEmailFn(): void {
		this.emailFn();
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=nfw9yna8xro!./shared/services/email/email-service/email.service.ts